import { useState } from 'react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Description,
  Field,
} from '@headlessui/react';
import { ChevronsUpDown, Check } from 'lucide-react';

export interface LookupItem { id: number; name: string }

export interface TypeaheadProps {
  items: LookupItem[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
  /** Accessible name for the input when no visible <label> is tied to it. */
  ariaLabel?: string;
  /** Marks the input invalid for assistive tech and draws a red border. */
  invalid?: boolean;
  /**
   * Error message shown under the input. It also marks the input invalid and,
   * being a Headless UI Description, is tied to the input with
   * aria-describedby (the Combobox ignores an aria-describedby passed in).
   */
  error?: string;
}

/**
 * Single-select typeahead on Headless UI's Combobox. Typing filters by
 * case-insensitive substring on the name. See MultiTypeahead for the
 * multi-select version.
 */
export const Typeahead = ({
  items,
  value,
  onChange,
  placeholder,
  ariaLabel,
  invalid,
  error,
}: TypeaheadProps) => {
  const isInvalid = invalid || !!error;
  const [query, setQuery] = useState('');
  const selected = items.find((i) => i.id === value) ?? null;
  const filtered = query === ''
    ? items
    : items.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Field>
      <Combobox value={selected} onChange={(item: LookupItem | null) => onChange(item?.id ?? null)}>
        <div className="relative">
          <div className={`relative w-full cursor-default rounded-md border bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${isInvalid ? 'border-red-500' : 'border-gray-300'}`}>
            <ComboboxInput
              className="w-full rounded-md border-none py-2 pl-3 pr-10 text-sm text-gray-900 outline-hidden"
              displayValue={(item: LookupItem | null) => item?.name ?? ''}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              aria-label={ariaLabel}
              aria-invalid={isInvalid || undefined}
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronsUpDown className="h-4 w-4 text-gray-400" />
            </ComboboxButton>
          </div>
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-hidden">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">Nothing found</div>
            ) : filtered.map((item) => (
              <ComboboxOption
                key={item.id}
                value={item}
                className={({ focus }) => `relative cursor-default select-none py-2 pl-9 pr-3 ${focus ? 'bg-blue-600 text-white' : 'text-gray-900'}`}
              >
                {({ selected: isSelected }) => (
                  <>
                    <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>{item.name}</span>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600 group-data-[focus]:text-white">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </div>
      </Combobox>
      {error && (
        <Description as="p" className="mt-1 text-sm text-red-600">
          {error}
        </Description>
      )}
    </Field>
  );
};

export default Typeahead;
